/* eslint-disable @typescript-eslint/no-empty-object-type */
import { Signal, computed } from '@angular/core';
import { SignalStoreFeature, signalStoreFeature, withComputed, withState } from '@ngrx/signals';

export type NamedRequestStatusState<Action extends string> = {
  [K in Action as `${K}RequestStatus`]: RequestStatus;
};

export type NamedRequestStatusComputed<Action extends string> = {
  [K in Action as `${K}IsPending`]: Signal<boolean>;
} & {
  [K in Action as `${K}IsFulfilled`]: Signal<boolean>;
} & {
  [K in Action as `${K}Error`]: Signal<string | null>;
};

export type Test = NamedRequestStatusComputed<'test'>;

export type RequestStatus = 'idle' | 'pending' | 'fulfilled' | { error: string };
export type RequestStatusState = { requestStatus: RequestStatus };

export function withRequestStatus<Action extends string>(config: {
  action: Action;
}): SignalStoreFeature<
  {
    methods: {};
    props: {};
    state: {};
  },
  {
    methods: {};
    props: NamedRequestStatusComputed<Action>;
    state: NamedRequestStatusState<Action>;
  }
>;
export function withRequestStatus<Action extends string>(config: {
  action: Action;
}): SignalStoreFeature {
  const { requestStatusKey, isFulfilledKey, isPendingKey, errorKey } = getRequestStatusKeys(config);

  return signalStoreFeature(
    withState({ [requestStatusKey]: 'idle' }),
    withComputed((state: Record<string, Signal<unknown>>) => {
      const requestStatus = state[requestStatusKey] as Signal<RequestStatus>;

      return {
        [isFulfilledKey]: computed(() => requestStatus() === 'fulfilled'),
        [isPendingKey]: computed(() => requestStatus() === 'pending'),
        [errorKey]: computed(() => {
          const status = requestStatus();

          return typeof status === 'object' ? status.error : null;
        }),
      };
    }),
  );
}

export function setPending<Action extends string>(config: {
  action: Action;
}): NamedRequestStatusState<Action> {
  return { [`${config.action}RequestStatus`]: 'pending' } as NamedRequestStatusState<Action>;
}

export function setFulfilled<Action extends string>(config: {
  action: Action;
}): NamedRequestStatusState<Action> {
  return { [`${config.action}RequestStatus`]: 'fulfilled' } as NamedRequestStatusState<Action>;
}

export function setError<Action extends string>(
  error: unknown,
  config: { action: Action },
): NamedRequestStatusState<Action> {
  if (error instanceof Error) {
    return {
      [`${config.action}RequestStatus`]: { error: error.message },
    } as NamedRequestStatusState<Action>;
  } else if (typeof error === 'string') {
    return {
      [`${config.action}RequestStatus`]: { error },
    } as NamedRequestStatusState<Action>;
  } else {
    return {
      [`${config.action}RequestStatus`]: { error: 'An unknown error occurred' },
    } as NamedRequestStatusState<Action>;
  }
}

export type RequestStatusKeys<Action extends string> = {
  requestStatusKey: `${Action}RequestStatus`;
  isFulfilledKey: `${Action}IsFulfilled`;
  isPendingKey: `${Action}IsPending`;
  errorKey: `${Action}Error`;
};

function getRequestStatusKeys<Action extends string>(config: {
  action: string;
}): RequestStatusKeys<Action> {
  return {
    requestStatusKey: `${config.action}RequestStatus` as `${Action}RequestStatus`,
    isFulfilledKey: `${config.action}IsFulfilled` as `${Action}IsFulfilled`,
    isPendingKey: `${config.action}IsPending` as `${Action}IsPending`,
    errorKey: `${config.action}Error` as `${Action}Error`,
  };
}
